<template>
  <header class="header">
    <div class="container">
      <div class="header-content">
        <!-- Logo -->
        <div class="logo">
          <router-link to="/" class="logo-link">
            <img src="@/assets/images/IPZhiXin.png" alt="IPZhiXin Logo" class="logo-image">
          </router-link>
        </div>

        <!-- 桌面导航 -->
        <nav class="nav-desktop">
          <ul class="nav-list">
            <li class="nav-item">
              <router-link :to="getLocalizedPath('/')" class="nav-link" exact-active-class="active" exact>
                {{ $t('nav.overview') }}
              </router-link>
            </li>
            <li class="nav-item">
              <router-link :to="getLocalizedPath('/company-intro')" class="nav-link" active-class="active">
                {{ $t('nav.companyIntro') }}
              </router-link>
            </li>
            <!-- 团队成员列表一级一级二级菜单 -->
            <li class="nav-item" @mouseenter="openDropdown('team')" @mouseleave="scheduleCloseDropdown('team')">
              <router-link :to="{ path: getLocalizedPath('/team'), query: { scrollToCategory: 'true' } }" class="nav-link" active-class="active">
                {{ $t('nav.team') }}
              </router-link>
              <div class="team-dropdowna" v-show="showTeamDropdown" @mouseenter="openDropdown('team')" @mouseleave="scheduleCloseDropdown('team')">
                <div class="team-members">
                  <div class="team-member" 
                       v-for="(member, index) in teamMembers" 
                       :key="index"
                       @click="goToTeamPage(member.categoryId)"
                       @mouseenter="hoveredMember = index"
                       @mouseleave="hoveredMember = null">
                    <div class="member-image">
                      <img :src="require(`@/${member.image}`)" :alt="$t('team.' + member.key)">
                    </div>
                    <div class="member-info">
                      <h3 class="member-name" :class="{'hovered': hoveredMember === index}">{{ $t('team.' + member.key) }}</h3>
                      <p class="member-education" 
                         v-for="(edu, eduIndex) in member.education" 
                         :key="eduIndex"
                         :class="{'hovered': hoveredMember === index}">{{ edu }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </li>
            <li class="nav-item" @mouseenter="openDropdown('services')" @mouseleave="scheduleCloseDropdown('services')">
              <router-link :to="getLocalizedPath('/practice-areas')" class="nav-link" active-class="active">
                {{ $t('nav.services') }}
              </router-link>
              <div class="team-dropdown" v-show="showServicesDropdown" @mouseenter="openDropdown('services')" @mouseleave="scheduleCloseDropdown('services')">
                <div class="team-members">
                  <div class="team-member" 
                       v-for="(service, index) in services" 
                       :key="index"
                       @click="goToPracticeAreasPage"
                       @mouseenter="hoveredService = index"
                       @mouseleave="hoveredService = null">
                    <div class="member-image">
                      <img :src="require(`@/${service.image}`)" :alt="$t('services.' + service.key)">
                    </div>
                    <div class="member-info">
                      <h3 class="member-name" :class="{'hovered': hoveredService === index}">{{ $t('services.' + service.key) }}</h3>
                      <p class="member-education" 
                         v-for="(edu, eduIndex) in service.education" 
                         :key="eduIndex"
                         :class="{'hovered': hoveredService === index}"> {{ $t(edu) }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </li>
            <li class="nav-item">
              <router-link :to="getLocalizedPath('/expertise-areas')" class="nav-link" active-class="active">
                {{ $t('nav.expertise') }}
              </router-link>
            </li>
            <li class="nav-item" @mouseenter="openDropdown('news')" @mouseleave="scheduleCloseDropdown('news')">
              <router-link :to="getLocalizedPath('/news')" class="nav-link" :class="{ active: isNewsActive }">
               {{ $t('nav.news') }}
              </router-link>
              <div class="news-dropdown" v-show="showNewsDropdown" @mouseenter="openDropdown('news')" @mouseleave="scheduleCloseDropdown('news')">
                <div class="news-menu">
                  <div class="news-menu-item" @click="goToNewsPage('/news')">
                      <h3 class="menu-item-title"> {{ $t('textnew01') }}</h3>
                  </div>
                  <div class="news-menu-item" @click="goToNewsPage('/hangye-news')">
                      <h3 class="menu-item-title">{{ $t('textnew02') }}</h3>
                  </div>
                  <div class="news-menu-item" @click="goToNewsPage('/huikui-news')">
                      <h3 class="menu-item-title">{{ $t('textnew03') }}</h3>
                  </div>
                  <div class="news-menu-item" @click="goToNewsPage('/xiaoqi-news')">
                      <h3 class="menu-item-title">{{ $t('textnew04') }}</h3>
                  </div>
                </div>
              </div>
            </li>
            <li class="nav-item">
              <router-link :to="getLocalizedPath('/contact')" class="nav-link" active-class="active">
                {{ $t('nav.contact') }}
              </router-link>
            </li>
          </ul>
        </nav>

        <!-- 语言切换器 -->
        <div class="language-switcher">
          <span 
            v-for="(lang, index) in languages"
            :key="lang.code"
            class="language-option"
            :class="{ active: $i18n.locale === lang.code }"
            @click="switchLanguage(lang.code)">
            {{ $t('lang.' + lang.key) }}
            <span v-if="index < languages.length - 1" class="language-separator">|</span>
          </span>
        </div>

        <!-- 移动端菜单按钮 -->
        <div class="mobile-menu-btn" @click="toggleMobileMenu">
          <i class="el-icon-menu"></i>
        </div>
      </div>
    </div>

    <!-- 移动端导航 -->
    <nav class="nav-mobile" v-show="mobileMenuOpen">
      <ul class="nav-mobile-list">
        <li class="nav-mobile-item">
          <router-link :to="getLocalizedPath('/')" tag="a" class="nav-mobile-link" exact-active-class="active" exact @click.native="closeMobileMenu">
            {{ $t('nav.overview') }}
          </router-link>
        </li>
        <li class="nav-mobile-item">
          <router-link :to="getLocalizedPath('/company-intro')" tag="a" class="nav-mobile-link" active-class="active" @click.native="closeMobileMenu">
            {{ $t('nav.companyIntro') }}
          </router-link>
        </li>
        <li class="nav-mobile-item">
          <router-link :to="{ path: getLocalizedPath('/team'), query: { scrollToCategory: 'true' } }" tag="a" class="nav-mobile-link" active-class="active" @click.native="closeMobileMenu">
            {{ $t('nav.team') }}
          </router-link>
        </li>
        <li class="nav-mobile-item">
          <router-link :to="getLocalizedPath('/practice-areas')" tag="a" class="nav-mobile-link" active-class="active" @click.native="closeMobileMenu">
            {{ $t('nav.services') }}
          </router-link>
        </li>
        <li class="nav-mobile-item">
          <router-link :to="getLocalizedPath('/expertise-areas')" tag="a" class="nav-mobile-link" active-class="active" @click.native="closeMobileMenu">
            {{ $t('nav.expertise') }}
          </router-link>
        </li>
        <li class="nav-mobile-item">
          <router-link :to="getLocalizedPath('/news')" tag="a" class="nav-mobile-link" active-class="active" @click.native="closeMobileMenu">
            {{ $t('nav.news') }}
          </router-link>
        </li>
        <li class="nav-mobile-item">
          <router-link :to="getLocalizedPath('/contact')" tag="a" class="nav-mobile-link" active-class="active" @click.native="closeMobileMenu">
            {{ $t('nav.contact') }}
          </router-link>
        </li>
      </ul>
    </nav>
  </header>
</template>

<script>
export default {
  name: 'Header',
  computed: {
    // 判断当前路由是否属于动态资讯相关页面
    isNewsActive() {
      const newsRoutes = ['/news', '/hangye-news', '/huikui-news', '/xiaoqi-news']
      return newsRoutes.some(route => this.$route.path.includes(route))
    },
    // 当前语言的显示文本 - 使用计算属性确保响应式更新
    currentLanguageText() {
      return this.$t('lang.' + this.getCurrentLangKey())
    }
  },
  data() {
    return {
      mobileMenuOpen: false,
      showTeamDropdown: false,
      hoveredMember: null,
      showServicesDropdown: false,
      hoveredService: null,
      showNewsDropdown: false,

      languages: [
        { code: 'zh', key: 'chinese' },
        { code: 'en', key: 'english' },
        { code: 'de', key: 'german' }
      ],
      // 悬浮下拉延迟关闭计时器
      hideTimers: {
        team: null,
        services: null,
        news: null
      },
      services: [{
          key: 'patent',
          education: [
            'textse01'
          ],
          image: 'assets/images/s_zhuanli.png'
        },
        {
          key: 'trademark',
          education: [
            'textse02'
          ],
          image: 'assets/images/s_shangbiao.png'
        },
        {
          key: 'trade',
          education: [
            'textse03'
          ],
          image: 'assets/images/s_shangye.png'
        },
        {
          key: 'rights',
          education: [
            'textse04',
            'textse05'
          ],
          image: 'assets/images/s_qinquan.png'
        }
      ],
      teamMembers: [
        {
          key: 'core',
          categoryId: 'core',
          education: [],
          image: 'assets/images/teamimg/hexin.png'
        },
        {
          key: 'patent',
          categoryId: 'patent',
          education: [],
          image: 'assets/images/teamimg/zhuanliimg.png'
        },
         {
          key: 'trademark',
          categoryId: 'trademark',
          education: [],
          image: 'assets/images/teamimg/shangbiaoteam.png'
        },
        {
          key: 'foreign',
          categoryId: 'foreign',
          education: [],
          image: 'assets/images/teamimg/shewaiimg.png'
        },
        {
          key: 'translation',
          categoryId: 'translation',
          education: [],
          image: 'assets/images/teamimg/fanyiimg.png'
        },
        {
          key: 'process',
          categoryId: 'process',
          education: [],
          image: 'assets/images/teamimg/liuchengimg.png'
        }
      ]
    }
  },
  methods: {
    // 检测浏览器语言并跳转
    detectBrowserLanguage() {
      // 确保在浏览器环境下运行
      if (typeof window === 'undefined') return;
      
      // 检查是否已经检测过语言，避免重复跳转
      if (sessionStorage.getItem('langDetected')) return;
      
      const browserLang = navigator.language || navigator.userLanguage;
      const supportedLangs = this.languages.map(lang => lang.code);
      let langToUse = 'en'; // 默认英语
      
      // 检查完整语言代码 (如zh-CN)
      if (supportedLangs.includes(browserLang.split('-')[0])) {
        langToUse = browserLang.split('-')[0];
      }
      
      // 如果当前语言不是检测到的语言，则跳转
      if (this.$i18n.locale !== langToUse) {
        this.switchLanguage(langToUse);
        sessionStorage.setItem('langDetected', 'true');
      }
    },

    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false
    },
    async goToTeamPage(categoryId = null) {
      const teamPath = this.getLocalizedPath('/team')
      // 如果有指定类别ID，则通过查询参数传递，并添加scrollToCategory标记
      if (categoryId) {
        await this.$router.push({ 
          path: teamPath, 
          query: { 
            category: categoryId,
            scrollToCategory: 'true' // 添加标记，指示需要滚动到类别区域
          } 
        })
      } else {
        // 如果没有指定类别，直接跳转到团队页面，并添加scrollToCategory标记
        if (this.$route.path !== teamPath) {
          await this.$router.push({ 
            path: teamPath,
            query: { 
              scrollToCategory: 'true' // 添加标记，指示需要滚动到类别区域
            }
          })
        } else {
          // 已在团队页时，也尝试滚动
          this.$nextTick(() => {
            this.scrollToTeamCategory()
          })
        }
      }
      this.showTeamDropdown = false
      // 跳转完成后滚动到类别区域
      this.scrollToTeamCategory()
    },
    goToPracticeAreasPage() {
      const practiceAreasPath = this.getLocalizedPath('/practice-areas')
      if (this.$route.path !== practiceAreasPath) {
        this.$router.push(practiceAreasPath)
      }
      this.showServicesDropdown = false
    },
    goToNewsPage(path) {
      const localizedPath = this.getLocalizedPath(path)
      if (this.$route.path !== localizedPath) {
        this.$router.push(localizedPath)
      }
      this.showNewsDropdown = false
    },
    // 语言切换 - 修复实时更新问题
    switchLanguage(langCode) {
      if (langCode !== this.$i18n.locale) {
        // 获取当前路径，去掉语言前缀
        let currentPath = this.$route.path
        const currentLang = this.$route.params.lang
        if (currentLang) {
          currentPath = currentPath.replace(`/${currentLang}`, '') || '/'
        }
        
        // 立即更新i18n语言设置
        this.$i18n.locale = langCode
        
        // 跳转到新语言的路径
        const newPath = `/${langCode}${currentPath === '/' ? '' : currentPath}`
        this.$router.push(newPath)
        
        // 强制更新组件以确保所有文本立即更新
        this.$nextTick(() => {
          this.$forceUpdate()
        })
      }
      this.showLanguageDropdown = false
    },
    // 获取本地化路径
    getLocalizedPath(path) {
      const currentLang = this.$i18n.locale
      return `/${currentLang}${path === '/' ? '' : path}`
    },
    // 获取当前语言的key
    getCurrentLangKey() {
      const lang = this.languages.find(l => l.code === this.$i18n.locale)
      return lang ? lang.key : 'chinese'
    },

    // 清除某类下拉的关闭计时器
    clearCloseTimer(type) {
      const key = String(type)
      if (this.hideTimers[key]) {
        clearTimeout(this.hideTimers[key])
        this.hideTimers[key] = null
      }
    },
    // 进入：立即打开并清除计时器
    openDropdown(type) {
      const key = String(type)
      this.clearCloseTimer(key)
      if (key === 'team') this.showTeamDropdown = true
      if (key === 'services') this.showServicesDropdown = true
      if (key === 'news') this.showNewsDropdown = true
    },
    // 离开：延迟关闭，避免从一级到二级时闪烁
    scheduleCloseDropdown(type) {
      const key = String(type)
      this.clearCloseTimer(key)
      this.hideTimers[key] = setTimeout(() => {
        if (key === 'team') this.showTeamDropdown = false
        if (key === 'services') this.showServicesDropdown = false
        if (key === 'news') this.showNewsDropdown = false
        this.hideTimers[key] = null
      }, 200)
    },
    // 切换路由时回到页面顶部（含兼容处理）
    scrollToTop() {
      if (typeof window !== 'undefined') {
        try {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
        } catch (e) {
          // 部分环境不支持 smooth，降级
          window.scrollTo(0, 0)
        }
        // 进一步兼容旧浏览器
        if (document && document.documentElement) {
          document.documentElement.scrollTop = 0
        }
        if (document && document.body) {
          document.body.scrollTop = 0
        }
      }
    },
    // 等待元素出现
    waitForElement(selector, timeout = 5000, interval = 50) {
      return new Promise((resolve, reject) => {
        const start = Date.now()
        const timer = setInterval(() => {
          const el = document.querySelector(selector)
          if (el) {
            clearInterval(timer)
            resolve(el)
          } else if (Date.now() - start >= timeout) {
            clearInterval(timer)
            reject(new Error('Element not found: ' + selector))
          }
        }, interval)
      })
    },
    // 滚动到团队类别区域，考虑粘性头部偏移
    async scrollToTeamCategory() {
      try {
        const el = await this.waitForElement('.team-category-section', 6000, 50)
        const header = document.querySelector('.header')
        const headerOffset = header ? header.offsetHeight : 0
        const rect = el.getBoundingClientRect()
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
        const top = rect.top + scrollTop - headerOffset - 8 // 额外减去一点间距
        window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' })
      } catch (e) {
        // 静默失败
      }
    },
    // 统一关闭所有下拉与移动菜单
    hideAllDropdowns() {
      this.showTeamDropdown = false
      this.showServicesDropdown = false
      this.showNewsDropdown = false
      this.mobileMenuOpen = false
    }
  },
  created() {
    this.detectBrowserLanguage();
  },

  watch: {
    // 监听路由变化：回到顶部并收起菜单
    $route(to) {
      // 如果是进入团队页且带有滚动标记，则不执行回到顶部，避免与滚动到类别区域冲突
      if (!(to && to.path && to.path.includes('/team') && to.query && to.query.scrollToCategory === 'true')) {
        this.scrollToTop()
      }
      this.hideAllDropdowns()
      
      // 如果路由参数中的语言发生变化，更新i18n语言设置
      if (to.params.lang && to.params.lang !== this.$i18n.locale) {
        this.$i18n.locale = to.params.lang
      }
    },
    // 监听语言变化，确保组件响应式更新
    '$i18n.locale'() {
      // 强制更新组件以确保所有翻译文本立即更新
      this.$forceUpdate()
    }
  }
}
</script>

<style scoped>
.header {
  background-color: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
}

.container {
  width: 1200px;
  margin: 0 auto;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

/* Logo样式 */
.logo {
  flex: 0 0 auto;
}

.logo-link {
  font-size: 1.8rem;
  font-weight: 700;
  text-decoration: none;
  color: #333;
  transition: color 0.3s ease;
}

.logo-link:hover {
  color: #FF631E;
}

.logo-link .highlight {
  color: #FF631E;
}

.logo-image {
  height: 60px;
  width: 168px;
  transition: opacity 0.3s ease;
}

.logo-link:hover .logo-image {
  opacity: 0.8;
}

/* 桌面导航样式 */
.nav-desktop {
  flex: 1;
  display: flex;
  justify-content: flex-end;
}

.nav-list {
  display: flex;
  gap: 2rem;
  list-style: none;
  margin: 0;
  padding: 0;
}

.nav-item {
  margin: 0;
}

.nav-link {
  display: block;
  font-size: 1rem;
  font-weight: bold;
  color: #333;
  text-decoration: none;
  padding: 0.5rem 0;
  position: relative;
  transition: color 0.3s ease;
}

.nav-link::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 2px;
  background-color: #FF631E;
  transition: width 0.3s ease, left 0.3s ease;
}

.nav-link:hover {
  color: #FF631E;
}

.nav-link:hover::after {
  width: 100%;
  left: 0;
}

.nav-link.active {
  color: #FF631E
}

.nav-link.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #FF631E;
}

/* 语言切换器 */
.language-switcher {
  margin-left: 2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.language-option {
  cursor: pointer;
  font-size: 0.9rem;
  color: #666;
  transition: color 0.3s ease;
}

.language-option:hover {
  color: #FF631E;
}

.language-option.active {
  color: #FF631E;
  font-weight: bold;
}

.language-separator {
  color: #ddd;
  user-select: none;
}

/* 移动端菜单按钮 */
.mobile-menu-btn {
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #333;
  cursor: pointer;
}

.mobile-menu-btn i {
  font-size: 1.5rem;
}

/* 移动端导航样式 */
.nav-mobile {
  background-color: white;
  border-top: 1px solid #e9ecef;
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
}

.nav-mobile-list {
  list-style: none;
  margin: 0;
  padding: 1rem 0;
}

.nav-mobile-item {
  margin: 0;
}

.nav-mobile-link {
  display: block;
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  text-decoration: none;
  padding: 1rem 2rem;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.nav-mobile-link:hover {
  background-color: #f8f9fa;
  color: #FF631E;
}

.nav-mobile-link.active {
  background-color: #f8f9fa;
  color: #FF631E;
}

/* 团队下拉菜单样式 */
.nav-item {
  position: relative;
}

.team-dropdown {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 400px;
  height: auto;
  box-sizing: border-box;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
  padding: 20px;
  z-index: 1001;

  margin-top: 10px;
}

.team-dropdowna {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 400px;
  height: auto;
  box-sizing: border-box;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
  padding: 20px;
  z-index: 1001;

  margin-top: 10px;
}

.team-dropdowna::before {
  content: '';
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 10px solid white;
}

.team-dropdown::before {
  content: '';
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 10px solid white;
}

.team-members {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.team-member {
  display: flex;
  align-items: center;
  padding: 0px 0px 0px 0px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.team-member:hover {
  /* background-color: #f9f9f9; */
}

.team-member:last-child {
  border-bottom: none;
}

.member-image {
  width: 60px;
  height: 80px;
  /* border-radius: 50%; */
  overflow: hidden;
  margin-right: 15px;
  flex-shrink: 0;
}

.member-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.member-info {
  flex: 1;
}

.member-name {
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #333;
  transition: color 0.3s ease;
}

.member-name.hovered {
  color: #FF631E;
}

.member-education {
  font-size: 0.9rem;
  color: #666;
  margin: 4px 0;
  line-height: 1.4;
  transition: color 0.3s ease;
}

.member-education.hovered {
  color: #FF631E;
}

/* 新闻下拉菜单样式 */
.news-dropdown {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  /* width: 166px; */
  min-width: 136px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
  padding: 15px;
  z-index: 1001;
  margin-top: 10px;
}

.news-dropdown::before {
  content: '';
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 10px solid white;
}

.news-menu {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.news-menu-item {
  padding: 12px 15px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  border-left: 3px solid transparent;
  border-bottom: 1px solid #eee;
}

.news-menu-item:hover {
  background-color: #f8f9fa;
  border-left-color: #FF631E;
}

.news-menu-item:last-child {
  border-bottom: none;
}

.menu-item-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.menu-item-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: #333;
  transition: color 0.3s ease;
}

.news-menu-item:hover .menu-item-title {
  color: #FF631E;
}

.menu-item-desc {
  font-size: 0.85rem;
  color: #666;
  margin: 0;
  line-height: 1.4;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .container {
    width: 90%;
  }
  
  .team-dropdown {
    width: 350px;
  }
  .team-dropdowna {
    width: 350px;
  }
}

@media (max-width: 992px) {
  .nav-desktop {
    display: none;
  }

  .mobile-menu-btn {
    display: block;
  }

  .language-switcher {
    margin-right: 1rem;
  }
}

@media (max-width: 768px) {
  .logo-link {
    font-size: 1.5rem;
  }
}

@media (max-width: 576px) {
  .header-content {
    padding: 0.5rem 0;
  }

  .logo-link {
    font-size: 1.3rem;
  }

  .language-switcher {
    margin-right: 0.5rem;
  }

  .language-current {
    padding: 0.3rem 0.5rem;
    font-size: 0.8rem;
  }
}
</style>